import axios from 'axios'
import AuthService from './auth-header'
import {
  resolvePromptForCall,
  spokenTextHasTokens,
  normalizeCallInfieldsMap,
  buildCallInfieldSchema,
  slimInitiateInfields,
  extractDeclaredInfieldKeys,
  pickSchemaCallInfields,
  resolveInitiateTranscriptLanguage,
  isBrokenHindiConfigAgent,
  isForbiddenEnglishCreateReuseId,
  canonicalizeIndusVoiceId,
  INDUS_MULTI_INDU_VOICE_ID,
  DEFAULT_ENGLISH_VOICE_ID,
  AI_CALL_TYPES,
  buildOfficialCallInfieldSchema,
  buildOfficialCallOutcomeSchema,
  ENGLISH_SCREENING_AGENT_NAME,
  ENGLISH_DRIVE_AGENT_NAME,
  ENGLISH_SCREENING_AGENT_NAME_ALIASES,
  ENGLISH_DRIVE_AGENT_NAME_ALIASES,
  DEFAULT_FIRST_MESSAGE,
  DEFAULT_SYSTEM_PROMPT,
  DEFAULT_DRIVE_FIRST_MESSAGE,
  DEFAULT_DRIVE_SYSTEM_PROMPT,
  SHORT_ENGLISH_SCREENING_SYSTEM,
  SHORT_ENGLISH_DRIVE_SYSTEM,
} from '@/utils/ai-calling-prompt-defaults'
import {
  formatInitiateGatewayFailure,
  looksLikeHtmlGatewayBody,
  classifyInitiateErrorBody,
} from '@/utils/ai-calling-errors'
import {
  buildInitiateCallPayload as assembleInitiateCallPayload,
  extractBoundAgentId,
  normalizePhoneForIndus,
  coerceRequiredInitiateId,
} from '@/utils/ai-calling-initiate'
import {
  AI_AGENT_CREATE_PATH,
  resolveCreateAgentLanguage,
  buildIndusCreatePostBodies,
  describeCreatePost,
  isCompleteIndusCreateConfig,
} from '@/utils/ai-calling-agent-create'
import {
  AiCallingConfigError,
  assertCreateReady,
  parseAiCallingSchemaConfig,
} from '@/utils/ai-calling-config-schema'
import { getAiCallingCatalogSnapshot } from '@/utils/ai-calling-catalog'

export {
  extractBoundAgentId,
  normalizePhoneForIndus,
  coerceRequiredInitiateId,
}

export function buildInitiateCallPayload(input = {}) {
  const body = assembleInitiateCallPayload(input)
  if (typeof console !== 'undefined' && console.info) {
    console.info('[ai-calling] initiate payload', {
      keys: Object.keys(body),
      keysLength: Object.keys(body).length,
      customer_number: body.customer_number,
      agent_number: body.agent_number,
      callback_url: body.callback_url,
      transcript: body.transcript,
      transcript_language: body.transcript_language,
      agent_config_keys: Object.keys(body.agent_config || {}),
    })
  }
  return body
}

function configErrorEnvelope(err, path) {
  return {
    status: 'ERROR',
    httpStatus: err.httpStatus || 400,
    path,
    message: err.message,
    errors: err.errors,
    missing: err.missing,
  }
}

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  // Local Vue (`npm run serve`) talks to public ATS — same convention as admin-config-service.
  // There is no local AI-calling backend in this repo. Production build uses the LAN ATS host.
  API_URL = 'https://ats.sattvahuman.in/api/'
} else {
  API_URL = 'http://192.168.0.30:5000/api/'
}

/**
 * ATS backend route conventions (verified against production Node script + Call Analytics):
 *
 * DEPLOYED today:
 *   POST ai-calls/initiate          — start one AI call (see scripts/ai-call-job-candidates.js)
 *   GET  ai-calls?job_id=…           — list AI calls for a job
 *   GET  ai-calls/:id                — single AI call + feedback
 *
 * DEPLOYED (see AI_CALLING_BACKEND.md):
 *   GET/POST/DELETE/PATCH ai-calls/agents/*  — Indus agent proxy (manage agents)
 *   GET  ai-calls/agents/:id/phone-numbers       — list inbound/outbound (when deployed)
 *   POST ai-calls/agents/:id/phone-numbers/attach — live ATS 405; client never POSTs this.
 *     Working Indus script never attaches — assign DID in the Indus portal.
 *   GET  ai-calling/agents/available         — combined agents for job reuse (read)
 *   GET/POST admin/config/ai-calling       — org defaults
 *
 * Agent list: GET ai-calling/agents/available only (live 200). Do not GET
 * ai-calls/agents (live 502). Do not probe other aliases.
 *
 * Batch campaign UI (Job tab) still uses ai-calling/* — backend may map these to ai-calls/initiate
 * loops or implement dedicated campaign routes later.
 *
 * Backend team: hold Indus api_key server-side; never expose in the browser.
 */

/** Primary prefix — matches working POST /api/ai-calls/initiate */
const AI_CALLS = 'ai-calls'
/** Legacy prefix used by batch/campaign UI stubs */
const AI_CALLING = 'ai-calling'

const BACKEND_NOT_DEPLOYED_MSG = 'Backend endpoint not deployed yet'
const DELETED_AGENTS_STORAGE_KEY = 'ats.indusDeletedAgentIds'

function readDeletedAgentStore() {
  try {
    if (typeof sessionStorage === 'undefined' || !sessionStorage) return []
    const raw = sessionStorage.getItem(DELETED_AGENTS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeDeletedAgentStore(ids) {
  try {
    if (typeof sessionStorage === 'undefined' || !sessionStorage) return
    sessionStorage.setItem(DELETED_AGENTS_STORAGE_KEY, JSON.stringify(ids))
  } catch (e) {
    /* private mode / SSR */
  }
}

const deletedIndusAgentIds = new Set(
  readDeletedAgentStore().map(id => String(id || '').trim()).filter(Boolean),
)

export function normalizeIndusAgentId(value) {
  return String(value || '').trim()
}

export function rememberDeletedIndusAgent(agentId) {
  const id = normalizeIndusAgentId(agentId)
  if (!id) return getDeletedIndusAgentIds()
  const already = [...deletedIndusAgentIds].some(existing => existing.toUpperCase() === id.toUpperCase())
  if (!already) deletedIndusAgentIds.add(id)
  const ids = getDeletedIndusAgentIds()
  writeDeletedAgentStore(ids)
  return ids
}

export function rememberDeletedIndusAgents(agentIds = []) {
  ;(Array.isArray(agentIds) ? agentIds : []).forEach(id => rememberDeletedIndusAgent(id))
  return getDeletedIndusAgentIds()
}

export function getDeletedIndusAgentIds() {
  return [...deletedIndusAgentIds]
}

export function isDeletedIndusAgent(agentId) {
  const id = normalizeIndusAgentId(agentId)
  if (!id) return false
  const key = id.toUpperCase()
  return [...deletedIndusAgentIds].some(existing => existing.toUpperCase() === key)
}

export function listedIndusAgentId(agent) {
  if (agent == null) return ''
  if (typeof agent === 'string' || typeof agent === 'number') {
    return normalizeIndusAgentId(agent)
  }
  return normalizeIndusAgentId(
    agent.agent_id || agent.agentId || agent.id || '',
  )
}

export function excludeDeletedIndusAgents(agents = []) {
  return (Array.isArray(agents) ? agents : []).filter(agent => !isDeletedIndusAgent(listedIndusAgentId(agent)))
}

export function clearDeletedIndusAgentsForTests() {
  deletedIndusAgentIds.clear()
  writeDeletedAgentStore([])
}

function collectErrorText(errOrRes) {
  const body = errOrRes?.response?.data ?? errOrRes
  const chunks = [
    errOrRes?.message,
    body && body.message,
    body && body.error,
    body && body.detail,
    body && body.data && body.data.message,
    typeof body === 'string' ? body : '',
  ]
  return chunks.filter(Boolean).join(' ')
}

/** GET/DELETE /ai-calls/agents/AGT_… (not phone-numbers or other suffixes). */
function isAgentByIdPath(path) {
  return /ai-calls\/agents\/[^/?]+$/i.test(String(path || ''))
}

export function isAgentAlreadyGone(errOrRes, path = '') {
  if (errOrRes?.alreadyGone || errOrRes?.status === 'ALREADY_GONE') return true
  const httpStatus = errOrRes?.httpStatus
    ?? errOrRes?.response?.status
  if (Number(httpStatus) !== 404) return false
  const text = collectErrorText(errOrRes)
  if (!/agent not found/i.test(text)) return false
  return !path || isAgentByIdPath(path)
}

export function isIndusAgentDeleteSuccess(res, agentId = '') {
  if (!res) return false
  const path = `ai-calls/agents/${String(agentId || '').trim()}`
  if (isAgentAlreadyGone(res, path)) return true
  const status = res.status
  if (status === 'OK' || status === 200 || status === 'success') return true
  const http = Number(res.httpStatus)
  return http === 200 || http === 204
}

/** User-facing copy when the agent list is missing (never include API paths). */
export const AGENTS_LIST_UNAVAILABLE_MSG =
  "Couldn't load your agents yet. You can still type an agent name/id, or create one in the Agents tab."

function friendlyNotReadyMessage(res, fallbackPath = '') {
  const path = String((res && res.path) || fallbackPath || '').toLowerCase()
  const rawMsg = String((res && res.message) || '')
  if (/agent list endpoint not available/i.test(rawMsg) || (path.includes('agents') && !path.includes('phone'))) {
    if (path.includes('agents/') || path.includes(':id')) {
      return "Couldn't update that agent. Please try again later."
    }
    return AGENTS_LIST_UNAVAILABLE_MSG
  }
  if (path.includes('phone-number') || path.includes('phone-numbers')) {
    return "Couldn't update the phone number for this agent. Please try again later."
  }
  if (path.includes('initiate')) {
    return formatInitiateGatewayFailure(res && (res.httpStatus || res.status), {
      bodyKind: res && res.bodyKind,
      message: res && res.message,
    })
  }
  if (path.includes('campaign')) {
    return "Couldn't start or update batch calling. Please try again."
  }
  if (path.includes('script')) {
    return "Couldn't save the script. Please try again."
  }
  if (path.includes('ai-calls') || path.includes('ai-calling')) {
    return "Couldn't complete that request. Please try again later."
  }
  return "This isn't available yet. Please try again later."
}

function looksLikeRawApiError(msg) {
  if (!msg) return false
  return /GET\s+\/api\/|POST\s+\/api\/|PATCH\s+\/api\/|DELETE\s+\/api\/|\/api\/ai-calls|\/api\/ai-calling/i.test(String(msg))
}

function authHeaders(extra = {}) {
  return { headers: AuthService.authHeader(), ...extra }
}

/** @param {import('axios').AxiosError|{ status?: number }} errOrRes */
export function isAiCallingNotDeployed(errOrRes) {
  if (isAgentAlreadyGone(errOrRes)) return false
  if (errOrRes?.status === 'NOT_DEPLOYED') return true
  const httpStatus = errOrRes?.httpStatus
    ?? errOrRes?.status
    ?? errOrRes?.response?.status
  return httpStatus === 404
}

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value)
  } catch (e) {
    return String(value)
  }
}

/** Pull a JSON object out of an nginx HTML 502 / mixed string body. */
function extractJsonFragment(value) {
  if (value == null) return null
  if (typeof value === 'object' && !Array.isArray(value)) return value
  const s = String(value)
  const start = s.indexOf('{')
  if (start < 0) return null
  let end = s.lastIndexOf('}')
  while (end > start) {
    try {
      const parsed = JSON.parse(s.slice(start, end + 1))
      if (parsed && typeof parsed === 'object') return parsed
    } catch (e) {
      /* keep shrinking */
    }
    end = s.lastIndexOf('}', end - 1)
  }
  return null
}

function stringifyMaybe(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'object') return safeJsonStringify(value)
  return String(value).trim()
}

function formatIndusCallFailure(obj) {
  if (!obj || typeof obj !== 'object') return ''
  const nested = obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)
    ? obj.data
    : {}
  const warning = stringifyMaybe(obj.warning || nested.warning)
  const message = stringifyMaybe(obj.message || nested.message)
  const error = stringifyMaybe(obj.error || nested.error)
  const parts = []
  if (warning) parts.push(warning)
  if (message && message !== warning) parts.push(message)
  if (error && error !== warning && error !== message) parts.push(error)
  if (parts.length) return sanitizeErrorText(parts.join(' — '))
  return ''
}

/** Strip nginx/HTML 502 pages and collapse whitespace for toasts. */
function sanitizeErrorText(value) {
  if (value == null) return ''
  let s = String(value).trim()
  if (!s) return ''
  if (/<html|<!doctype|<title>/i.test(s)) {
    const fromJson = extractJsonFragment(s)
    const indus = formatIndusCallFailure(fromJson)
    if (indus) return indus
    const title = s.match(/<title>([^<]+)<\/title>/i)
    if (title && title[1]) return title[1].replace(/\s+/g, ' ').trim()
    s = s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }
  if (s.length > 280) s = `${s.slice(0, 277)}…`
  return s
}

function isGenericHttpStatusText(text) {
  return /^(502|503|405|400|500|bad gateway|service unavailable|method not allowed|request failed( with status code \d+)?|502 bad gateway|503 service unavailable)$/i
    .test(String(text || '').trim())
}

/**
 * SattvaATS initiate success: HTTP 200/202 and `{ status: "OK" }`.
 * Do not treat a truthy `res.data` as success (error envelopes can include data).
 * Do not invent soft-success on 502.
 */
export function isInitiateCallSuccess(res) {
  if (!res) return false
  if (isAiCallingNotDeployed(res)) return false
  const http = Number(res.httpStatus)
  if (Number.isFinite(http) && http >= 400) return false
  const outer = String(res.status || '').toUpperCase()
  if (outer === 'FAILED' || outer === 'ERROR') return false
  return outer === 'OK' || outer === 'SUCCESS' || res.status === 202 || res.status === 200
    || res.status === 'Accepted'
}

/** 405 on attach = path exists but POST is not implemented. Do not treat as hard failure. */
export function isAiCallingAttachSkipped(res) {
  if (!res) return false
  if (res.skipped === true || String(res.status || '').toUpperCase() === 'SKIPPED') return true
  return res.httpStatus === 405
}

function isAtsStatusEnvelope(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  const status = String(obj.status || '').toUpperCase()
  return status === 'ERROR' || status === 'FAILED'
    || (Object.prototype.hasOwnProperty.call(obj, 'data') && !!(obj.message || obj.error))
}

/**
 * Peel ATS `{ status, message, data }` until the Indus / FastAPI payload remains.
 * `data` is often the real 422 (`detail` loc/msg, string, or object).
 */
function unwrapAtsErrorInner(body, depth = 0) {
  if (body == null || depth > 6) return body
  if (typeof body === 'string') {
    const trimmed = body.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return unwrapAtsErrorInner(JSON.parse(trimmed), depth + 1)
      } catch (e) {
        return body
      }
    }
    return body
  }
  if (typeof body !== 'object') return body
  if (Array.isArray(body)) return body
  if (isAtsStatusEnvelope(body) && body.data != null && body.data !== body) {
    return unwrapAtsErrorInner(body.data, depth + 1)
  }
  const nested = body.upstream ?? body.indus_error ?? body.indus
    ?? body.cause ?? body.details ?? body.error_description
  if (nested != null && nested !== body && (typeof nested === 'object' || typeof nested === 'string')) {
    const peeled = unwrapAtsErrorInner(nested, depth + 1)
    if (peeled != null && peeled !== nested) return peeled
    if (typeof nested === 'string' && nested.trim()) return nested
    if (typeof nested === 'object') return peeled
  }
  return body
}

/**
 * FastAPI / Indus 422 bodies use `detail` (string or [{ loc, msg }]).
 * ATS envelopes use `message` / `error` and nest the real error in `data`.
 */
function formatValidationDetail(detail) {
  if (detail == null) return ''
  if (typeof detail === 'string') return detail.trim()
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      if (typeof item === 'string') return item
      if (!item || typeof item !== 'object') return ''
      const loc = Array.isArray(item.loc)
        ? item.loc.filter(part => part !== 'body' && part !== 'query').join('.')
        : ''
      const msg = item.msg || item.message || ''
      if (msg && loc) return `${msg} (${loc})`
      return msg || loc
    }).filter(Boolean).join('; ')
  }
  if (typeof detail === 'object') {
    if (detail.detail != null && detail.detail !== detail) {
      const nested = formatValidationDetail(detail.detail)
      if (nested) return nested
    }
    if (detail.data != null && detail.data !== detail) {
      const inner = formatValidationDetail(detail.data)
      if (inner) return inner
    }
    return String(detail.msg || detail.message || detail.error || '').trim()
  }
  return ''
}

/** FastAPI detail, string, or compact JSON — never stop at the ATS wrapper alone. */
function formatInnerErrorPayload(inner) {
  if (inner == null || inner === '') return ''
  const fromDetail = formatValidationDetail(inner)
  if (fromDetail) return sanitizeErrorText(fromDetail)
  if (typeof inner === 'string') return sanitizeErrorText(inner)
  if (typeof inner === 'object') return sanitizeErrorText(safeJsonStringify(inner))
  return sanitizeErrorText(inner)
}

/** User-facing message from the ATS envelope (no invented 502 / callback classification). */
export function getAiCallingErrorMessage(res, fallbackPath = '') {
  if (isAgentAlreadyGone(res, res?.path || fallbackPath)) {
    return 'Agent not found'
  }
  if (isAiCallingNotDeployed(res) || looksLikeRawApiError(res?.message)) {
    // eslint-disable-next-line no-console
    console.error('[AiCallingService] not ready', {
      path: res?.path || fallbackPath,
      httpStatus: res?.httpStatus || res?.status,
      message: res?.message,
    })
    return friendlyNotReadyMessage(res, fallbackPath)
  }
  const inner = unwrapAtsErrorInner(res)
  const wrapperMsg = sanitizeErrorText(res?.message || res?.error || '')
  const innerText = formatInnerErrorPayload(inner)
  const hasDistinctInner = inner != null && inner !== res && !!innerText && innerText !== wrapperMsg
  const detailMsg = sanitizeErrorText(formatValidationDetail(res?.detail)
    || (hasDistinctInner ? innerText : '')
    || formatValidationDetail(res?.data?.detail))
  const errors = res?.errors
    || (inner && typeof inner === 'object' ? inner.errors : null)
    || res?.data?.errors
  const errorsMsg = Array.isArray(errors)
    ? sanitizeErrorText(formatValidationDetail(errors))
    : sanitizeErrorText(typeof errors === 'string' ? errors : '')
  const httpStatus = res?.httpStatus
  if (httpStatus === 502 || httpStatus === 503) {
    const html = (res && res.bodyKind === 'html')
      || looksLikeHtmlGatewayBody(res && (res.message || res.data || res.error))
    if (html && /initiate/i.test(String(fallbackPath || res?.path || ''))) {
      return formatInitiateGatewayFailure(httpStatus, {
        bodyKind: 'html',
        html: true,
        message: wrapperMsg,
      })
    }
    const indusFail = formatIndusCallFailure(inner) || formatIndusCallFailure(res)
    const useful = [indusFail, innerText, detailMsg, errorsMsg].find(t =>
      t
      && !isGenericHttpStatusText(t)
      && !looksLikeRawApiError(t)
      && !/no warning text|upstream error/i.test(t)
      && !String(t).trim().startsWith('{'),
    )
    if (useful) {
      return appendLanguageMismatchHint(`HTTP ${httpStatus}: ${useful}`, fallbackPath)
    }
    if (/initiate/i.test(String(fallbackPath || res?.path || ''))) {
      return formatInitiateGatewayFailure(httpStatus, {
        bodyKind: res && res.bodyKind,
        message: wrapperMsg,
      })
    }
    return friendlyNotReadyMessage(res, fallbackPath)
  }
  if (httpStatus === 405) {
    const useful = [innerText, wrapperMsg].find(t =>
      t && !isGenericHttpStatusText(t) && !looksLikeRawApiError(t),
    )
    if (useful) return useful
    return 'This action is not supported on the server yet.'
  }
  if (httpStatus === 422 || httpStatus === 400) {
    const validation = detailMsg || errorsMsg || (hasDistinctInner ? innerText : '')
    if (validation) {
      return looksLikeRawApiError(validation) ? friendlyNotReadyMessage(res, fallbackPath) : validation
    }
    const generic = wrapperMsg || res?.data?.message || res?.data?.error
    if (generic && !looksLikeRawApiError(generic)) {
      return String(generic)
    }
    return 'Some required fields are missing or invalid. Please check and try again.'
  }
  if (hasDistinctInner && !looksLikeRawApiError(innerText)) {
    return innerText
  }
  const msg = res?.message
    || res?.data?.message
    || res?.error
    || res?.data?.error
    || detailMsg
    || errorsMsg
  if (msg) {
    if (looksLikeRawApiError(msg)) {
      // eslint-disable-next-line no-console
      console.error('[AiCallingService] raw API error', { path: fallbackPath, message: msg })
      return friendlyNotReadyMessage(res, fallbackPath)
    }
    return appendLanguageMismatchHint(msg, fallbackPath)
  }
  if (res?.status === 'FAILED' || res?.status === 'ERROR') {
    const failed = formatIndusCallFailure(inner) || formatIndusCallFailure(res) || 'Request failed'
    return appendLanguageMismatchHint(failed, fallbackPath)
  }
  return 'Request failed'
}

function appendLanguageMismatchHint(message, fallbackPath) {
  const text = String(message || '').trim()
  if (!text || !/initiate/i.test(String(fallbackPath || ''))) return text
  if (/Indus-multi-indu|Config Language must match/i.test(text)) return text
  if (/voice provider rejected|language|marathi|\bmr\b|voice/i.test(text)) {
    return `${text} Set this agent's Indus Config to Language English and Voice Indus-multi-indu (ATS cannot PATCH).`
  }
  return text
}

/**
 * Success toast copy after POST ai-calls/initiate (test call).
 * @param {*} res
 * @param {{ mockMode?: boolean }} [opts]
 */
export function getTestCallSuccessMessage(/* res, opts */) {
  return 'Test call initiated — the candidate number should ring shortly.'
}

/**
 * Explain why a test call cannot be placed before hitting the API.
 * @param {{ jobAgentId?: string, did?: string, mockMode?: boolean }} ctx
 */
export function getTestCallBlockedMessage(ctx = {}) {
  if (!ctx.jobAgentId) {
    return 'Job agent is not ready. Link an org agent in Admin Settings → AI Calling, then try again.'
  }
  if (!ctx.did) {
    return 'No outbound phone number is set. Add numbers in Admin Settings → AI Calling, then try again.'
  }
  return 'Enter a valid candidate phone number to place a test call.'
}

function alreadyGoneEnvelope(path, err) {
  const body = err?.response?.data
  return {
    ...(body && typeof body === 'object' ? body : {}),
    status: 'OK',
    alreadyGone: true,
    httpStatus: 404,
    message: 'Agent not found',
    path,
  }
}

function notDeployedEnvelope(path, err) {
  if (isAgentAlreadyGone(err, path)) {
    return alreadyGoneEnvelope(path, err)
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[AiCallingService] ${BACKEND_NOT_DEPLOYED_MSG}: ${API_URL}${path}`,
    '\nBackend team: implement ATS proxy to Indus agent APIs (login + POST /api/agents/create).',
    'See header comment in src/services/ai-calling-service.js.',
  )
  const body = err?.response?.data
  return {
    ...(body && typeof body === 'object' ? body : {}),
    status: 'NOT_DEPLOYED',
    httpStatus: 404,
    message: BACKEND_NOT_DEPLOYED_MSG,
    path,
  }
}

function normalizeError(err, path = '', options = {}) {
  if (err?.response?.status === 404) {
    if (isAgentAlreadyGone(err, path)) {
      return alreadyGoneEnvelope(path, err)
    }
    return notDeployedEnvelope(path, err)
  }
  let body = err.response?.data
  const httpStatus = err.response?.status
  const bodyKind = classifyInitiateErrorBody(body)
  if (typeof body === 'string') {
    const trimmed = body.trim()
    const fragment = extractJsonFragment(trimmed)
    if (fragment) {
      body = fragment
    } else if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        body = JSON.parse(trimmed)
      } catch (e) {
        body = { message: sanitizeErrorText(trimmed) }
      }
    } else {
      body = { message: sanitizeErrorText(trimmed || err.message || 'Request failed') }
    }
  }
  const base = body && typeof body === 'object'
    ? body
    : { message: sanitizeErrorText(body || err.message || 'Request failed') }
  const inner = unwrapAtsErrorInner(base)
  const formatted = formatInnerErrorPayload(inner) || sanitizeErrorText(formatValidationDetail(base))
  const quiet = options.quiet === true
  if (httpStatus && httpStatus >= 400 && !quiet) {
    // eslint-disable-next-line no-console
    if (httpStatus === 502 || httpStatus === 503) {
      console.warn(`[AiCallingService] ${httpStatus} on ${path}`)
    } else {
      console.warn(`[AiCallingService] ${httpStatus} on ${path}`, err.response?.data)
    }
  }
  const fallbackByStatus = httpStatus === 502 || httpStatus === 503
    ? 'The calling service could not complete the request (upstream error).'
    : httpStatus === 405
      ? 'This action is not supported on the server yet.'
      : httpStatus === 422
        ? 'Request failed validation (422). Check required agent fields and types.'
        : sanitizeErrorText(err.message || 'Request failed')
  const formattedLooksLikeJson = typeof formatted === 'string' && formatted.trim().startsWith('{')
  const resolved = (formatted && formatted !== base.message && !isGenericHttpStatusText(formatted)
    && !formattedLooksLikeJson)
    ? formatted
    : sanitizeErrorText(base.message || formatted || base.error || fallbackByStatus)
  return {
    ...base,
    httpStatus,
    bodyKind,
    path: path || undefined,
    message: resolved && !isGenericHttpStatusText(resolved) && !String(resolved).trim().startsWith('{')
      ? resolved
      : fallbackByStatus,
  }
}

/**
 * Extract agent rows from ATS/Indus list responses (various envelope shapes).
 * @param {*} raw
 * @returns {Array<Object|string>}
 */
/**
 * Normalize Indus GET /api/agents/{id}/phone-numbers (or ATS proxy envelope).
 * @param {*} res
 * @returns {{ ok: boolean, agentId?: string, inbound?: Object|null, outbound?: Object|null }}
 */
export function parseAgentPhoneNumbersResponse(res) {
  if (!res || isAiCallingNotDeployed(res)) {
    return { ok: false, inbound: null, outbound: null }
  }
  const nested = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
    ? res.data
    : res
  if (nested.status === 'FAILED' || nested.status === 'ERROR') {
    return { ok: false, inbound: null, outbound: null }
  }
  return {
    ok: true,
    agentId: nested.agent_id || nested.agentId,
    inbound: nested.inbound ?? null,
    outbound: nested.outbound ?? null,
  }
}

/** Last 10 digits for Indian display (E.164 or local). */
export function formatIndianPhoneDisplay(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  if (digits.length >= 10) return digits.slice(-10)
  return digits
}

export const SPOKEN_INITIATE_KEYS = [
  'script',
  'firstMessage',
  'first_message',
  'starting_instructions',
  'systemPrompt',
  'system_prompt',
]

export function collectSpokenTokenLeaks(payload = {}) {
  const leaks = []
  SPOKEN_INITIATE_KEYS.forEach((key) => {
    if (spokenTextHasTokens(payload[key])) leaks.push(key)
  })
  const flows = [].concat(payload.script_flow || [], payload.conversation_flow || [])
  flows.forEach((step, i) => {
    const text = step && (step.spokenText || step.text || '')
    if (spokenTextHasTokens(text)) leaks.push(`script_flow[${i}]`)
  })
  const cfg = payload.agent_config && typeof payload.agent_config === 'object'
    ? payload.agent_config
    : {}
  if (spokenTextHasTokens(cfg.system_prompt) || spokenTextHasTokens(cfg.starting_instructions)) {
    leaks.push('agent_config.prompts')
  }
  const clickCfg = payload.indus_click2call && payload.indus_click2call.agent_config
    ? payload.indus_click2call.agent_config
    : {}
  if (spokenTextHasTokens(clickCfg.system_prompt) || spokenTextHasTokens(clickCfg.starting_instructions)) {
    leaks.push('indus_click2call.agent_config.prompts')
  }
  return leaks
}

/** Copy infields onto every alias Indus / ATS has used. */
export function attachInfieldAliases(payload, values) {
  const target = payload && typeof payload === 'object' ? payload : {}
  const map = normalizeCallInfieldsMap(values)
  const schema = buildCallInfieldSchema(map)
  target.infields = { ...map }
  target.call_infields = { ...map }
  target.call_infield = schema
  target.dynamic_variables = { ...map }
  target.custom_data = { ...map }
  target.metadata = { ...(target.metadata || {}), ...map }
  return { payload: target, map, schema }
}

function sanitizeSpokenText(text, infields) {
  const raw = String(text || '')
  if (!spokenTextHasTokens(raw)) return raw.trim()
  return resolvePromptForCall(raw, infields || {}).trim()
}

/**
 * Build Indus POST /api/calls/click2call body (phase-1 Python script shape).
 * agent_config is runtime infields only — the stored org agent already has the
 * {{token}} script. Do not nest firstMessage / system_prompt here.
 * @param {Object} opts
 * @returns {Object}
 */
export function buildIndusClick2CallPayload(opts = {}) {
  const customerNumber = normalizePhoneForIndus(
    opts.customer_number || opts.customerNumber || opts.contact_no || opts.contactNo,
  )
  const agentId = String(opts.agent_number || opts.agentNumber || opts.agent_id || opts.agentId || '').trim()
  const did = normalizePhoneForIndus(opts.did || opts.outbound_did || opts.outboundDid || '')
  const transcript = opts.transcript !== false
  const payload = {
    customer_number: customerNumber,
    agent_number: agentId,
    did,
    transcript,
  }
  const lang = opts.transcript_language || opts.transcriptLanguage
  if (transcript && lang) {
    payload.transcript_language = String(lang).trim()
  }
  const rawInfields = opts.call_infields || opts.callInfields || opts.infields || {}
  const declared = extractDeclaredInfieldKeys(opts)
  const values = slimInitiateInfields(rawInfields, declared, {
    callType: opts.callType || opts.call_type,
  })
  if (values && Object.keys(values).length) {
    payload.agent_config = values
    payload.call_infields = pickSchemaCallInfields(values, declared)
  }
  return payload
}

/**
 * Test-call candidate_id: phone-matched job candidate, else first job candidate, else job_id.
 * SattvaATS requires a numeric candidate_id; job_id as last-resort sentinel is accepted.
 */
export function resolveTestCallCandidateId({ matchedCandidateId, fallbackCandidateId, jobId } = {}) {
  const matched = Number(matchedCandidateId)
  if (Number.isFinite(matched) && matched > 0) return matched
  const fallback = Number(fallbackCandidateId)
  if (Number.isFinite(fallback) && fallback > 0) return fallback
  const jobN = Number(jobId)
  if (Number.isFinite(jobN) && jobN > 0) return jobN
  return jobId ?? fallbackCandidateId ?? matchedCandidateId ?? ''
}

/** Campaign start: same one-agent + infields contract as initiate. */
export function buildCampaignStartPayload(input = {}) {
  const src = input && typeof input === 'object' ? input : {}
  const declared = extractDeclaredInfieldKeys({
    call_infield: src.declared_infields || src.declaredInfields,
    ...(src.agent && typeof src.agent === 'object' ? src.agent : {}),
  })
  const callInfields = slimInitiateInfields(
    src.call_infields || src.callInfields || src.infields || src.agent_config || {},
    declared,
    { callType: src.callType || src.call_type, config: src.config },
  )
  return {
    callType: src.callType || src.call_type,
    statuses: src.statuses,
    agent_id: extractBoundAgentId(src),
    transcript_language: resolveInitiateTranscriptLanguage(src),
    call_infields: { ...callInfields },
    agent_config: { ...callInfields },
  }
}

/** ATS-only keys — persist on ATS, must NOT be forwarded to Indus (extra=forbid → 422). */
const ATS_CREATE_AGENT_EXTRA_KEYS = [
  'prompt_source',
  'prompt_template_id',
  'call_type',
  'phone_numbers',
  'attach_phone_numbers',
  'created_by_emp_id',
  'created_by_name',
]

function omitNullSchemaEnums(item) {
  if (!item || typeof item !== 'object') return item
  const next = { ...item }
  if (next.field_enum == null) delete next.field_enum
  if (next.outcome_enum == null) delete next.outcome_enum
  return next
}

function stripAtsOnlyCreateFields(body) {
  if (!body || typeof body !== 'object') return body
  const next = { ...body }
  ATS_CREATE_AGENT_EXTRA_KEYS.forEach((key) => {
    delete next[key]
  })
  return next
}

function extraFieldErrorText(res) {
  const inner = unwrapAtsErrorInner(res)
  const detail = res?.detail ?? inner?.detail ?? inner
  const chunks = [
    formatValidationDetail(detail),
    typeof detail === 'string' ? detail : '',
    Array.isArray(detail) ? detail.map(item => item?.type || item?.msg || '').join(' ') : '',
    res?.message,
    res?.data?.message,
    typeof inner === 'string' ? inner : safeJsonStringify(inner),
  ]
  return chunks.filter(Boolean).join(' ')
}

function looksLikeExtraFieldError(res) {
  return /extra (fields|inputs) (are )?not permitted|value_error\.extra|extra_forbidden/i
    .test(extraFieldErrorText(res))
}

/** FastAPI loc paths for extra=forbid items, e.g. ['prompt_source'] or ['agent_in','agent_name']. */
function extractExtraFieldLocs(res) {
  const inner = unwrapAtsErrorInner(res)
  const detail = Array.isArray(res?.detail)
    ? res.detail
    : (Array.isArray(inner?.detail) ? inner.detail : (Array.isArray(inner) ? inner : []))
  const items = Array.isArray(detail) ? detail : []
  const locs = []
  items.forEach((item) => {
    const isExtra = /extra/i.test(item?.msg || '') || /extra/i.test(item?.type || '')
    if (!isExtra || !Array.isArray(item.loc)) return
    const loc = item.loc.filter(part => part !== 'body' && part !== 'query')
    if (loc.length) locs.push(loc)
  })
  if (!locs.length) {
    const text = extraFieldErrorText(res)
    ATS_CREATE_AGENT_EXTRA_KEYS.forEach((key) => {
      if (text.includes(key)) locs.push([key])
    })
    ;['name', 'description', 'agent_description'].forEach((key) => {
      if (new RegExp(`\\b${key}\\b`).test(text)) locs.push(['agent_in', key])
    })
  }
  return locs
}

function omitByLocs(obj, locs) {
  const next = JSON.parse(JSON.stringify(obj || {}))
  locs.forEach((loc) => {
    let cursor = next
    for (let i = 0; i < loc.length - 1; i += 1) {
      if (!cursor || typeof cursor !== 'object') return
      cursor = cursor[loc[i]]
    }
    if (cursor && typeof cursor === 'object' && loc.length) {
      delete cursor[loc[loc.length - 1]]
    }
  })
  return next
}

function sanitizeAgentName(name) {
  return String(name || '')
    .replace(/[·•]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120)
}

/** Live Indus enum: OUTBOUND | INBOUND | WHATSAPP. Job/Admin create default OUTBOUND. */
function normalizeAgentType(value) {
  const s = String(value || '').trim().toUpperCase()
  if (s === 'INBOUND' || s === 'WHATSAPP') return s
  return 'OUTBOUND'
}

function isAgentCreateHttpError(res) {
  const http = res?.httpStatus
  if (http === 422 || http === 400) return true
  const st = String(res?.status || '').toUpperCase()
  return st === 'ERROR' || st === 'FAILED'
}

/**
 * Indus POST /api/agents/create body.
 * Python test_ai_agent.py sends agent_in + system_prompt/starting_instructions.
 * Official docs add optional call_infield / call_outcome (field_name / outcome_name).
 * Prompt-only creates land with Infields 0/15, Outcomes 0/15, Hi + Indus-hi-maya.
 * @param {Object} input
 * @returns {Object}
 */
export function buildCreateAgentPayload(input = {}) {
  const src = input && typeof input === 'object' ? input : {}
  const ready = assertCreateReady(src)
  const agentInSrc = src.agent_in && typeof src.agent_in === 'object' ? src.agent_in : {}
  const agentConfig = src.agent_config && typeof src.agent_config === 'object' ? src.agent_config : {}

  const name = sanitizeAgentName(ready.name)
  const description = String(
    agentInSrc.agent_description || agentInSrc.description || src.agent_description || src.description || '',
  ).trim()
  const agentType = normalizeAgentType(
    agentInSrc.agent_type || agentInSrc.agentType || src.agent_type || src.agentType,
  )
  const callType = src.callType || src.call_type || agentInSrc.call_type || agentConfig.call_type
  const language = ready.language
  const voice = ready.voice
  const systemPrompt = String(
    agentConfig.system_prompt || src.system_prompt || src.systemPrompt || '',
  ).trim()
  const startingInstructions = String(
    agentConfig.starting_instructions
    || src.starting_instructions
    || src.firstMessage
    || src.first_message
    || '',
  ).trim()

  const ttsSrc = agentConfig.tts_config && typeof agentConfig.tts_config === 'object'
    ? agentConfig.tts_config
    : {}
  const ttsConfig = {
    voice_id: voice,
    voice,
    model: String(ttsSrc.model || src.model || '').trim(),
  }

  const config = {
    system_prompt: systemPrompt,
    starting_instructions: startingInstructions,
    language,
    voice,
    voice_id: voice,
    tts_config: ttsConfig,
    stt_config: { language },
    realtime: src.realtime !== false && agentConfig.realtime !== false,
  }

  const callInfield = ready.infields
  const callOutcome = ready.outcomes
  const agentIn = {
    agent_name: name,
    agent_type: agentType,
  }
  if (description) agentIn.agent_description = description

  return {
    agent_in: agentIn,
    agent_config: config,
    call_infield: callInfield,
    call_outcome: callOutcome,
  }
}

/**
 * Live Indus/ATS agent rows only. Never suggestedAgents, jobAgents, or
 * org assignment leftovers — those keep deleted portal agents on Admin cards.
 */
export function extractLiveIndusAgentsList(raw) {
  let list = []
  if (!raw) {
    list = []
  } else if (Array.isArray(raw.agents)) {
    list = raw.agents
  } else if (Array.isArray(raw.indusAgents)) {
    list = raw.indusAgents
  } else {
    const nested = raw.data
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      if (Array.isArray(nested.agents)) list = nested.agents
      else if (Array.isArray(nested.indusAgents)) list = nested.indusAgents
    } else if (Array.isArray(raw)) {
      list = raw
    } else if (Array.isArray(raw.data)) {
      list = raw.data
    }
  }
  return excludeDeletedIndusAgents(list)
}

export function extractIndusAgentsList(raw) {
  const live = extractLiveIndusAgentsList(raw)
  if (live.length) return live
  if (!raw) return []
  if (Array.isArray(raw.data)) return raw.data
  if (Array.isArray(raw.agents)) return raw.agents
  const nested = raw.data
  if (nested && typeof nested === 'object') {
    if (Array.isArray(nested.agents)) return nested.agents
    if (Array.isArray(nested.indusAgents)) return nested.indusAgents
    if (Array.isArray(nested.jobAgents)) return nested.jobAgents
    if (Array.isArray(nested.suggestedAgents)) return nested.suggestedAgents
    if (Array.isArray(nested.items)) return nested.items
    if (Array.isArray(nested.results)) return nested.results
  }
  return []
}

/**
 * Normalize list/available agent payloads to { agents, suggestedAgents }.
 * @param {*} res
 * @returns {Object|null}
 */
function normalizeAvailableAgentsEnvelope(res) {
  if (!res || isAiCallingNotDeployed(res)) return null
  const nested = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
    ? res.data
    : res
  const suggestedAgents = Array.isArray(nested.suggestedAgents) ? nested.suggestedAgents : []
  const agents = Array.isArray(nested.agents)
    ? nested.agents
    : (Array.isArray(nested.indusAgents) ? nested.indusAgents : [])
  if (!agents.length && !suggestedAgents.length) return null
  return {
    status: res.status || 'OK',
    data: { agents, suggestedAgents },
  }
}

/**
 * Extract AI call rows from GET /api/ai-calls list envelope.
 * @param {*} res
 * @returns {{ ok: boolean, calls: Array, error?: string, notDeployed?: boolean }}
 */
export function parseAiCallsListResponse(res) {
  if (!res) {
    return { ok: false, calls: [], error: 'Empty response from AI calls API.' }
  }
  if (isAiCallingNotDeployed(res)) {
    return {
      ok: false,
      calls: [],
      notDeployed: true,
      error: getAiCallingErrorMessage(res, 'ai-calls'),
    }
  }
  const status = res.status
  const explicitFail = status === 'FAILED' || status === 'ERROR' || status === 'failed'
  if (explicitFail) {
    return {
      ok: false,
      calls: [],
      error: getAiCallingErrorMessage(res, 'ai-calls'),
    }
  }

  let rows = []
  if (Array.isArray(res)) {
    rows = res
  } else if (Array.isArray(res.data)) {
    rows = res.data
  } else if (Array.isArray(res.calls)) {
    rows = res.calls
  } else if (res.data && typeof res.data === 'object') {
    if (Array.isArray(res.data.calls)) rows = res.data.calls
    else if (Array.isArray(res.data.items)) rows = res.data.items
    else if (Array.isArray(res.data.results)) rows = res.data.results
  }

  return { ok: true, calls: rows }
}

/**
 * Normalize one ai_calls row for Call History UI.
 * @param {Object} row
 * @param {Array<{ id?: *, candidate_id?: *, name?: string, candidate_name?: string }>} [candidates]
 * @returns {Object}
 */
export function normalizeAiCallRow(row, candidates = []) {
  if (!row || typeof row !== 'object') {
    return {
      id: '',
      candidateId: '',
      jobId: '',
      candidateLabel: '—',
      status: 'unknown',
      durationSeconds: null,
      durationLabel: '—',
      createdAt: null,
      timeLabel: '—',
      contactNo: '',
      outcomeLabel: '',
      raw: row,
    }
  }

  const candidateId = row.candidate_id ?? row.candidateId ?? ''
  const pool = Array.isArray(candidates) ? candidates : []
  const match = pool.find(c => {
    if (!c) return false
    const cid = c.id ?? c.candidate_id ?? c.candidateId
    return cid != null && String(cid) === String(candidateId)
  })
  const candidateName = row.candidate_name
    || row.candidateName
    || match?.name
    || match?.candidate_name
    || match?.candidateName
    || ''
  const contactNo = row.contact_no || row.contactNo || row.customer_number || ''
  const candidateLabel = candidateName
    || (candidateId ? `Candidate #${candidateId}` : '')
    || (contactNo ? formatIndianPhoneDisplay(contactNo) : '—')

  const durationSeconds = row.duration_seconds != null
    ? Number(row.duration_seconds)
    : (row.durationSeconds != null ? Number(row.durationSeconds) : null)
  let durationLabel = '—'
  if (Number.isFinite(durationSeconds) && durationSeconds >= 0) {
    const mins = Math.floor(durationSeconds / 60)
    const secs = Math.round(durationSeconds % 60)
    durationLabel = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
  }

  const createdAt = row.createdAt || row.created_at || row.updatedAt || row.updated_at || null
  let timeLabel = '—'
  if (createdAt) {
    try {
      const d = new Date(createdAt)
      if (!Number.isNaN(d.getTime())) {
        timeLabel = d.toLocaleString(undefined, {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      }
    } catch (e) {
      timeLabel = String(createdAt)
    }
  }

  const status = String(row.status || row.call_status || 'unknown').toLowerCase()
  const outcomes = row.outcomes || row.feedback || row.call_outcomes || null
  let outcomeLabel = ''
  if (outcomes && typeof outcomes === 'object' && !Array.isArray(outcomes)) {
    const cs = outcomes.call_status || outcomes.callStatus || outcomes.interested
    if (cs != null && cs !== '') outcomeLabel = String(cs)
  }

  return {
    id: row.id || row.call_id || row.callId || '',
    callId: row.call_id || row.callId || '',
    candidateId,
    jobId: row.job_id ?? row.jobId ?? '',
    candidateLabel,
    status,
    durationSeconds: Number.isFinite(durationSeconds) ? durationSeconds : null,
    durationLabel,
    createdAt,
    timeLabel,
    contactNo,
    outcomeLabel,
    raw: row,
  }
}

/**
 * Normalize list-agents API responses for Admin Settings UI.
 * @param {*} res
 * @returns {{ ok: boolean, agents: Array, error?: string, notDeployed?: boolean, empty?: boolean }}
 */
/** Map available-agents rows ({ agent_id, name, is_default }) to Admin list shape. */
function listShapeFromAvailableAgents(availRes) {
  const envelope = normalizeAvailableAgentsEnvelope(availRes)
  const raw = envelope
    ? (Array.isArray(envelope.data && envelope.data.agents) ? envelope.data.agents : [])
    : extractLiveIndusAgentsList(availRes)
  if (!raw.length) return null
  const agents = raw.map((a) => {
    if (typeof a === 'string') {
      return { agent_id: a, name: a, status: 'unknown', is_default: false }
    }
    const id = a.agent_id || a.agentId || a.id || ''
    return {
      agent_id: id,
      name: a.name || a.agent_name || a.title || id,
      status: a.status || a.state || 'unknown',
      is_default: !!(a.is_default || a.isDefault),
      updated_at: a.updated_at || a.updatedAt || a.modified_at || '',
      created_by_emp_id: a.created_by_emp_id || a.createdByEmpId || '',
      created_by_name: a.created_by_name || a.createdByName || '',
      prompt_source: a.prompt_source || a.promptSource || '',
      call_type: a.call_type || a.callType || '',
    }
  }).filter(a => a.agent_id)
  if (!agents.length) return null
  return { status: 'OK', data: agents, agents }
}

export function parseIndusAgentsListResponse(res) {
  if (!res) {
    return { ok: false, agents: [], error: 'Empty response from agents API.' }
  }
  if (isAiCallingNotDeployed(res)) {
    // eslint-disable-next-line no-console
    console.error('[AiCallingService] agents list not available', {
      httpStatus: res?.httpStatus,
      path: res?.path,
      message: res?.message,
    })
    return {
      ok: false,
      agents: [],
      notDeployed: true,
      error: AGENTS_LIST_UNAVAILABLE_MSG,
    }
  }

  const agents = extractLiveIndusAgentsList(res)
  const status = res.status
  const httpStatus = res.httpStatus ?? (typeof status === 'number' ? status : undefined)
  const explicitOk = status === 'OK' || status === 200 || status === 'success'
  const explicitFail = status === 'FAILED' || status === 'ERROR' || status === 'failed'
    || (typeof httpStatus === 'number' && httpStatus >= 400)

  if (explicitOk || agents.length > 0) {
    return { ok: true, agents, empty: agents.length === 0 }
  }

  if (explicitFail) {
    return {
      ok: false,
      agents: [],
      error: getAiCallingErrorMessage(res, 'ai-calls/agents'),
    }
  }

  const msg = res.message || res.data?.message
  if (msg) {
    return { ok: false, agents: [], error: msg }
  }

  return {
    ok: false,
    agents: [],
    error: AGENTS_LIST_UNAVAILABLE_MSG,
  }
}

/**
 * Single-URL request. Do not probe aliases — one logical API, one path.
 * @param {'get'|'post'|'put'|'patch'|'delete'} method
 * @param {string} path
 * @param {*} [bodyOrConfig]
 * @param {Object} [config] axios config for mutating methods, or `{ quiet: true }` for GET/DELETE
 */
async function requestOne(method, path, bodyOrConfig, config) {
  const quiet = !!(config && config.quiet)
  try {
    const url = `${API_URL}${path}`
    let res
    if (method === 'get' || method === 'delete') {
      res = await axios[method](url, bodyOrConfig || authHeaders())
    } else if (config !== undefined) {
      const { quiet: _quiet, ...rest } = config
      const axiosConfig = Object.keys(rest).length ? rest : authHeaders()
      res = await axios[method](url, bodyOrConfig, axiosConfig)
    } else {
      res = await axios[method](url, bodyOrConfig, authHeaders())
    }
    return res.data
  } catch (err) {
    const httpStatus = err.response?.status
    if (!quiet && (httpStatus === 422 || httpStatus === 400)) {
      const body = err.response?.data
      const inner = unwrapAtsErrorInner(body)
      const formatted = formatInnerErrorPayload(inner) || formatValidationDetail(body)
      // eslint-disable-next-line no-console
      console.error(`[AiCallingService] ${httpStatus} on ${path}`, {
        formatted,
        innerData: safeJsonStringify(inner),
        detail: inner,
        response: body,
        sent: (method !== 'get' && method !== 'delete') ? bodyOrConfig : undefined,
      })
    }
    if (httpStatus === 404) {
      if (isAgentAlreadyGone(err, path)) {
        return alreadyGoneEnvelope(path, err)
      }
      return notDeployedEnvelope(path, err)
    }
    return normalizeError(err, path, { quiet })
  }
}

function listedAgentName(agent) {
  if (!agent || typeof agent !== 'object') return ''
  const nested = agent.agent_in && typeof agent.agent_in === 'object' ? agent.agent_in : {}
  return String(
    agent.name || agent.agent_name || agent.title || nested.agent_name || nested.name || '',
  ).trim()
}

function findListedAgentByName(agents, name) {
  const aliases = [name]
    .concat(name === ENGLISH_SCREENING_AGENT_NAME ? ENGLISH_SCREENING_AGENT_NAME_ALIASES : [])
    .concat(name === ENGLISH_DRIVE_AGENT_NAME ? ENGLISH_DRIVE_AGENT_NAME_ALIASES : [])
    .map(n => String(n || '').trim().toLowerCase())
    .filter(Boolean)
  if (!aliases.length) return null
  const matches = (agents || []).filter((a) => {
    const id = extractBoundAgentId(a)
    if (isForbiddenEnglishCreateReuseId(id)) return false
    return aliases.includes(listedAgentName(a).toLowerCase())
  })
  return matches[0] || null
}

let ensureEnglishAgentsInflight = null

/**
 * AI Calling API client (async batch campaigns via Indus).
 * Backend owns Indus credentials and the 2-number dial pool.
 */
class AiCallingService {
  // ── Deployed ai-calls routes (Node script reference) ─────────────────────

  /**
   * Initiate a single AI call — POST /api/ai-calls/initiate.
   * Body is the Indus click2call contract (ATS must forward as-is).
   * Success is the backend envelope `{ status: "OK" }` / HTTP 200–202.
   * @param {Object} payload mapped to { customer_number, agent_number, callback_url, transcript, transcript_language, agent_config }
   */
  async initiateCall(payload) {
    let body
    try {
      body = buildInitiateCallPayload(payload)
    } catch (err) {
      if (err instanceof AiCallingConfigError) {
        return configErrorEnvelope(err, `${AI_CALLS}/initiate`)
      }
      throw err
    }
    const missing = []
    if (!body.agent_number) missing.push('agent')
    if (!body.customer_number) missing.push('phone')
    if (!body.callback_url) missing.push('callback')
    if (!body.transcript_language) missing.push('language')
    if (missing.length) {
      return {
        status: 'ERROR',
        httpStatus: 400,
        path: `${AI_CALLS}/initiate`,
        message: `Cannot start the call — missing ${missing.join(', ')}.`,
      }
    }
    try {
      const res = await axios.post(`${API_URL}${AI_CALLS}/initiate`, body, authHeaders())
      return res.data
    } catch (err) {
      const normalized = normalizeError(err, `${AI_CALLS}/initiate`)
      if (normalized.httpStatus === 502 || normalized.httpStatus === 503) {
        // eslint-disable-next-line no-console
        console.warn('[AiCallingService] initiate upstream failed (no retry)', {
          httpStatus: normalized.httpStatus,
          bodyKind: normalized.bodyKind || 'unknown',
          htmlGateway: normalized.bodyKind === 'html',
          agent_number: body.agent_number,
          customer_number: body.customer_number ? 'set' : 'missing',
          callback_url: body.callback_url ? 'set' : 'missing',
        })
      }
      return normalized
    }
  }

  /**
   * List AI calls. Same live GET /api/ai-calls the job tab uses.
   * Params today: job_id, candidate_id, call_id, limit.
   * Omit job_id for a user/org-scoped list when the backend allows it.
   * @param {{ job_id?: string|number, candidate_id?: string|number, call_id?: string, limit?: number }} [params]
   */
  async listAiCalls(params = {}) {
    try {
      const res = await axios.get(`${API_URL}${AI_CALLS}`, {
        ...authHeaders(),
        params,
      })
      return res.data
    } catch (err) {
      const suffix = params.job_id != null && params.job_id !== '' ? '?job_id=' : ''
      return normalizeError(err, `${AI_CALLS}${suffix}`)
    }
  }

  /**
   * List AI calls for a job (or filtered by candidate_id / call_id / limit).
   * Deployed: GET /api/ai-calls?job_id=…
   * Backend query params today: job_id, candidate_id, call_id, limit
   * (no status / date-range filters — callers filter client-side for analytics).
   * @param {string|number} jobId
   * @param {{ candidate_id?: string|number, call_id?: string, limit?: number }} [params]
   */
  async listJobAiCalls(jobId, params = {}) {
    return this.listAiCalls({ job_id: jobId, ...params })
  }

  /**
   * Single AI call detail (status, duration, outcomes, transcript when available).
   * Deployed: GET /api/ai-calls/:id
   * @param {string|number} aiCallId
   */
  async getAiCall(aiCallId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLS}/${encodeURIComponent(aiCallId)}`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLS}/:id`)
    }
  }

  // ── Batch / campaign UI (ai-calling/* — may not be deployed) ─────────────

  async getQueueStatus() {
    try {
      const res = await axios.get(`${API_URL}${AI_CALLING}/queue/status`, authHeaders())
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/queue/status`)
    }
  }

  /**
   * @param {string|number} jobId
   * @param {string[]} [statuses]
   */
  async getEligibleCandidates(jobId, statuses = []) {
    try {
      const params = {}
      if (statuses && statuses.length) {
        params.statuses = statuses.join(',')
      }
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/jobs/${jobId}/candidates`,
        { ...authHeaders(), params },
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/candidates`)
    }
  }

  /**
   * @param {string|number} jobId
   */
  async getJobScripts(jobId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/jobs/${jobId}/scripts`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/scripts`)
    }
  }

  /**
   * Persist per-job call scripts / prompts.
   * PUT body is merged into jobScripts JSON. Known fields:
   *   callType, script|firstMessage, systemPrompt,
   *   lastCallType, presetId, customBrief, otherBrief, promptSource,
   *   promptMeta { [callType]: { presetId, customBrief, otherBrief, promptSource } },
   *   promptHistory[] (client-capped; no dedicated versions API — see AI_CALLING_BACKEND.md §6)
   * @param {string|number} jobId
   * @param {Object} payload
   */
  async saveJobScript(jobId, payload) {
    try {
      const res = await axios.put(
        `${API_URL}${AI_CALLING}/jobs/${jobId}/scripts`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/scripts`)
    }
  }

  /**
   * Start an async batch campaign. Expects 202 / OK with campaignId.
   * @param {string|number} jobId
   * @param {{ callType: string, statuses: string[], script?: string }} payload
   */
  async startCampaign(jobId, payload) {
    try {
      const body = buildCampaignStartPayload(payload)
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/jobs/${jobId}/campaigns`,
        body,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/campaigns`)
    }
  }

  /**
   * @param {string|number} jobId
   */
  async listJobCampaigns(jobId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/jobs/${jobId}/campaigns`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/campaigns`)
    }
  }

  /**
   * @param {string|number} campaignId
   */
  async getCampaign(campaignId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/campaigns/${campaignId}`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/campaigns/:id`)
    }
  }

  /**
   * Per-job agent — GET /api/ai-calling/jobs/:jobId/agent
   * @param {string|number} jobId
   */
  async getJobAgent(jobId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/jobs/${encodeURIComponent(jobId)}/agent`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/agent`)
    }
  }

  /**
   * Create or link a per-job agent.
   * @param {string|number} jobId
   * @param {{ reuse_agent_id?: string, source_job_id?: string, create?: boolean }} payload
   */
  async setJobAgent(jobId, payload = {}) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/jobs/${encodeURIComponent(jobId)}/agent`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/agent`)
    }
  }

  /**
   * List agents available for reuse (Indus + tracked job agents).
   * GET /api/ai-calling/agents/available?job_id= only — do not follow with
   * GET /api/ai-calls/agents (live 502).
   * @param {string|number} [jobId]
   */
  async listAvailableAgents(jobId) {
    const path = `${AI_CALLING}/agents/available`
    const axiosConfig = {
      ...authHeaders(),
      params: jobId != null && jobId !== '' ? { job_id: jobId } : {},
    }
    const res = await requestOne('get', path, axiosConfig)

    const normalized = normalizeAvailableAgentsEnvelope(res)
    if (normalized) {
      const agents = excludeDeletedIndusAgents(normalized.data && normalized.data.agents)
      return {
        ...normalized,
        data: {
          ...(normalized.data || {}),
          agents,
        },
      }
    }

    if (isAiCallingNotDeployed(res) || res?.httpStatus === 404) {
      return {
        ...res,
        status: 'NOT_DEPLOYED',
        httpStatus: 404,
        path,
        message: AGENTS_LIST_UNAVAILABLE_MSG,
        data: { agents: [], suggestedAgents: [] },
        empty: true,
      }
    }

    const emptyOk = !res?.httpStatus || res.httpStatus < 400
      || res.status === 'OK' || res.status === 200 || res.status === 'success'
    if (emptyOk) {
      return { status: 'OK', data: { agents: [], suggestedAgents: [] }, empty: true }
    }

    return res
  }

  /**
   * Check if another user has an active AI Calling session on this job.
   * @param {string|number} jobId
   */
  async getActiveSession(jobId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/jobs/${encodeURIComponent(jobId)}/active-session`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/active-session`)
    }
  }

  /**
   * Register or heartbeat an active session on this job tab.
   * @param {string|number} jobId
   * @param {{ campaignId?: string }} [payload]
   */
  async registerActiveSession(jobId, payload = {}) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/jobs/${encodeURIComponent(jobId)}/active-session`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/active-session`)
    }
  }

  /**
   * Release active session when leaving the job AI Calling tab.
   * @param {string|number} jobId
   */
  async releaseActiveSession(jobId) {
    try {
      const res = await axios.delete(
        `${API_URL}${AI_CALLING}/jobs/${encodeURIComponent(jobId)}/active-session`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/jobs/:id/active-session`)
    }
  }

  /**
   * @param {string|number} campaignId
   */
  async pauseCampaign(campaignId) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/campaigns/${campaignId}/pause`,
        {},
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/campaigns/:id/pause`)
    }
  }

  /**
   * @param {string|number} campaignId
   */
  async cancelCampaign(campaignId) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/campaigns/${campaignId}/cancel`,
        {},
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/campaigns/:id/cancel`)
    }
  }

  // ── Indus agent proxy (GET/POST /api/ai-calls/agents) ─

  /**
   * List Indus voice agents via GET /api/ai-calling/agents/available only.
   * Shares listAvailableAgents — never GET /api/ai-calls/agents (live 502).
   * @returns {Promise<Object>}
   */
  async listIndusAgents() {
    const avail = await this.listAvailableAgents()
    const live = extractLiveIndusAgentsList(avail)
    if (live.length) {
      return listShapeFromAvailableAgents(avail) || { status: 'OK', data: live, agents: live }
    }
    if (avail?.empty || avail?.status === 'OK' || avail?.status === 200 || avail?.status === 'success'
      || !avail?.httpStatus || avail.httpStatus < 400) {
      return { status: 'OK', data: [], agents: [], empty: true }
    }
    return avail
  }

  /**
   * Fetch a single agent by id via ATS proxy.
   * @param {string} agentId
   * @returns {Promise<Object>}
   */
  async getAgent(agentId) {
    const enc = encodeURIComponent(agentId)
    return requestOne('get', `${AI_CALLS}/agents/${enc}`)
  }

  /**
   * Create Indus agent via ATS proxy (backend holds Indus credentials).
   * Documented path only: POST /api/ai-calls/agents.
   *
   * First wire body is 09-07: agent_name + prompts + tts_config.voice_id +
   * one call_infield / call_outcome. Fat language/voice keys stay off the wire
   * (extra=forbid). Never POST agent_config: {} or a prompts-only flat body.
   * @param {Object} payload
   * @returns {Promise<Object>} e.g. { status: 'OK', data: { agent_id: 'AGT_...' } }
   */
  async createIndusAgent(payload) {
    const createPath = AI_AGENT_CREATE_PATH
    let body
    try {
      body = buildCreateAgentPayload(payload)
    } catch (err) {
      if (err instanceof AiCallingConfigError) {
        return configErrorEnvelope(err, createPath)
      }
      throw err
    }
    const plannedInfields = Array.isArray(body.call_infield) ? body.call_infield : []
    const plannedOutcomes = Array.isArray(body.call_outcome) ? body.call_outcome : []
    const selectedVoice = body.agent_config && (body.agent_config.voice_id || body.agent_config.voice)
    const selectedLanguage = body.agent_config && body.agent_config.language
    const posts = buildIndusCreatePostBodies(body)
    const attempts = [
      posts.official,
      posts.officialNoSchema,
      posts.officialName,
      posts.officialNameNoSchema,
      posts.promptsOnly,
    ].filter(item => item && isCompleteIndusCreateConfig(item.agent_config))
    const first = attempts[0]
    if (!first) {
      return configErrorEnvelope(
        new AiCallingConfigError('Create agent is missing system_prompt or starting_instructions.'),
        createPath,
      )
    }
    if (typeof console !== 'undefined' && console.info) {
      console.info('[AiCallingService] create agent sent', {
        ...describeCreatePost(first),
        language: selectedLanguage,
        voice: selectedVoice,
        infield_count: plannedInfields.length,
        outcome_count: plannedOutcomes.length,
        call_infield: plannedInfields.map(f => f.field_name),
        call_outcome: plannedOutcomes.map(o => o.outcome_name),
      })
    }
    let postedCreate = first
    let res = await requestOne('post', createPath, postedCreate)
    for (let i = 1; i < attempts.length && isAgentCreateHttpError(res); i += 1) {
      postedCreate = attempts[i]
      res = await requestOne('post', createPath, postedCreate)
    }
    if (isAgentCreateHttpError(res) && looksLikeExtraFieldError(res)) {
      const stripped = stripAtsOnlyCreateFields(omitByLocs(postedCreate, extractExtraFieldLocs(res)))
      if (JSON.stringify(stripped) !== JSON.stringify(postedCreate)) {
        // eslint-disable-next-line no-console
        console.warn('[AiCallingService] 422 extra-field — retrying without forbidden keys', {
          formatted: getAiCallingErrorMessage(res, createPath),
        })
        postedCreate = stripped
        res = await requestOne('post', createPath, stripped)
      }
    }
    if (isAgentCreateHttpError(res) && typeof console !== 'undefined' && console.warn) {
      const formatted = getAiCallingErrorMessage(res, createPath)
      const firstSent = describeCreatePost(first)
      const lastSent = describeCreatePost(postedCreate)
      console.warn('[AiCallingService] create agent failed', {
        httpStatus: res?.httpStatus,
        status: res?.status,
        message: formatted,
        ats_swallowed_indus_detail: /failed to create indus agent/i.test(formatted || '')
          && (res?.data == null),
        first: firstSent,
        sent: lastSent,
      })
      res = { ...res, first_sent: firstSent, sent: lastSent }
    }
    const createdId = extractBoundAgentId(res)
    if (createdId && !isAgentCreateHttpError(res)) {
      const postedTts = postedCreate && postedCreate.agent_config && postedCreate.agent_config.tts_config
      if (!postedTts && selectedVoice) {
        await this.applyIndusCreateVoice(createdId, {
          voice: selectedVoice,
          language: selectedLanguage,
        })
      }
      const sentFirstOnCreate = !!(postedCreate && (postedCreate.call_infield || postedCreate.call_outcome))
      const remainingInfields = sentFirstOnCreate ? plannedInfields.slice(1) : plannedInfields
      const remainingOutcomes = sentFirstOnCreate ? plannedOutcomes.slice(1) : plannedOutcomes
      if (remainingInfields.length || remainingOutcomes.length) {
        const seeded = await this.seedAgentSchemas(createdId, {
          infields: remainingInfields,
          outcomes: remainingOutcomes,
        })
        res = {
          ...res,
          schema_seed: seeded,
        }
      }
    }
    return res
  }

  /**
   * Official Indus: PUT /api/agents/{id}/config/tts_config
   * Voice must be Indus-multi-indu (Indu · REALTIME · Multi).
   */
  async applyIndusMultiVoice(agentId) {
    return this.applyIndusCreateVoice(agentId, {
      voice: INDUS_MULTI_INDU_VOICE_ID,
      language: 'en',
    })
  }

  /** After prompts-only create, set the selected voice / transcript language. */
  async applyIndusCreateVoice(agentId, { voice, language } = {}) {
    const id = String(agentId || '').trim()
    const voiceId = String(voice || '').trim()
    const lang = String(language || '').trim()
    if (!id || !voiceId) return { ok: false }
    const enc = encodeURIComponent(id)
    const ttsBody = { voice_id: voiceId }
    const attempts = [
      { method: 'put', path: `${AI_CALLS}/agents/${enc}/config/tts_config`, body: ttsBody },
      { method: 'put', path: `${AI_CALLING}/agents/${enc}/config/tts_config`, body: ttsBody },
      {
        method: 'post',
        path: `${AI_CALLS}/agents/${enc}/configs?publish=true`,
        body: {
          tts_config: ttsBody,
          ...(lang ? { stt_config: { language: lang } } : {}),
        },
      },
    ]
    let last = null
    for (let i = 0; i < attempts.length; i += 1) {
      const a = attempts[i]
      last = await requestOne(a.method, a.path, a.body, { quiet: true })
      const ok = last && !isAiCallingNotDeployed(last) && !(last.httpStatus >= 400)
        && String(last.status || '').toUpperCase() !== 'ERROR'
      if (ok) return { ok: true, path: a.path, res: last }
    }
    return { ok: false, res: last }
  }

  /**
   * Official Indus: POST remaining /call_infields and /call_outcomes (one object each).
   */
  async seedAgentSchemas(agentId, { infields = [], outcomes = [] } = {}) {
    const id = String(agentId || '').trim()
    if (!id) return { addedInfields: 0, addedOutcomes: 0 }
    const enc = encodeURIComponent(id)

    const alreadyExists = (last) => {
      const http = last && last.httpStatus
      if (http === 409) return true
      const text = extraFieldErrorText(last)
      return /already exists|duplicate|already (been )?added/i.test(text)
    }
    const postUntilWorks = async (kind, item, paths) => {
      let last = null
      for (let p = 0; p < paths.length; p += 1) {
        last = await requestOne('post', paths[p], item, { quiet: true })
        const ok = last && !isAiCallingNotDeployed(last) && !(last.httpStatus >= 400)
          && String(last.status || '').toUpperCase() !== 'ERROR'
          && String(last.status || '').toUpperCase() !== 'FAILED'
        if (ok || alreadyExists(last)) return { ok: true, path: paths[p], res: last }
        if (!isAiCallingNotDeployed(last) && last && last.httpStatus && last.httpStatus < 500 && last.httpStatus !== 404) {
          return { ok: false, path: paths[p], res: last, stop: false }
        }
      }
      return { ok: false, path: paths[0], res: last, stop: true }
    }

    const infieldPaths = [
      `${AI_CALLS}/agents/${enc}/call_infields`,
      `${AI_CALLING}/agents/${enc}/call_infields`,
    ]
    const outcomePaths = [
      `${AI_CALLS}/agents/${enc}/call_outcomes`,
      `${AI_CALLING}/agents/${enc}/call_outcomes`,
    ]

    let addedInfields = 0
    let addedOutcomes = 0
    let workingInfieldPath = infieldPaths
    let workingOutcomePath = outcomePaths
    let stopInfields = false
    let stopOutcomes = false

    for (let i = 0; i < infields.length; i += 1) {
      if (stopInfields) break
      const field = omitNullSchemaEnums(infields[i])
      const result = await postUntilWorks('infield', field, workingInfieldPath)
      if (result.ok) {
        addedInfields += 1
        workingInfieldPath = [result.path]
      } else if (result.stop) {
        stopInfields = true
      }
    }
    for (let i = 0; i < outcomes.length; i += 1) {
      if (stopOutcomes) break
      const outcome = omitNullSchemaEnums(outcomes[i])
      const result = await postUntilWorks('outcome', outcome, workingOutcomePath)
      if (result.ok) {
        addedOutcomes += 1
        workingOutcomePath = [result.path]
      } else if (result.stop) {
        stopOutcomes = true
      }
    }
    if ((infields.length || outcomes.length) && typeof console !== 'undefined' && console.info) {
      console.info('[AiCallingService] seed agent schemas', {
        agent_id: id,
        infields_posted: addedInfields,
        outcomes_posted: addedOutcomes,
        infields_planned: infields.length,
        outcomes_planned: outcomes.length,
      })
    }
    return { addedInfields, addedOutcomes }
  }

  /** @deprecated use createIndusAgent */
  async saveAgent(payload) {
    return this.createIndusAgent(payload)
  }

  /**
   * One DELETE /ai-calls/agents/:id. No GET/PATCH retry.
   * 404 + "Agent not found" = already gone on Indus (success).
   * @param {string} agentId
   */
  async deleteIndusAgent(agentId) {
    const id = String(agentId || '').trim()
    if (!id) {
      return { status: 'OK', alreadyGone: true, httpStatus: 404, message: 'Agent not found' }
    }
    const path = `${AI_CALLS}/agents/${encodeURIComponent(id)}`
    const res = await requestOne('delete', path)
    if (isIndusAgentDeleteSuccess(res, id)) {
      rememberDeletedIndusAgent(id)
    }
    if (isAgentAlreadyGone(res, path)) {
      return { ...res, status: 'OK', alreadyGone: true }
    }
    return res
  }

  /**
   * GET attached phone numbers for an Indus agent (inbound/outbound).
   * @param {string} agentId
   */
  async getAgentPhoneNumbers(agentId) {
    const enc = encodeURIComponent(agentId)
    return requestOne('get', `${AI_CALLS}/agents/${enc}/phone-numbers`, undefined, { quiet: true })
  }

  /**
   * Detach a phone number from an Indus agent via ATS proxy.
   * @param {string} agentId
   * @param {{ phone_number?: string, direction?: 'inbound'|'outbound' }} [payload]
   */
  async detachAgentPhoneNumber(agentId, payload = {}) {
    const enc = encodeURIComponent(agentId)
    return requestOne('post', `${AI_CALLS}/agents/${enc}/phone-numbers/detach`, payload)
  }

  /**
   * Set org default agent for batch calling.
   * Documented path only: PATCH /api/ai-calls/agents/default.
   * Fallback: caller merges into admin AI Calling config.
   * @param {string} agentId
   */
  async setDefaultAgent(agentId) {
    const body = { agent_id: agentId, agentId }
    return requestOne('patch', `${AI_CALLS}/agents/default`, body)
  }

  /**
   * Render agent prompt with job/candidate metadata (Indus config render proxy).
   * @param {string} agentId
   * @param {Object} metadata e.g. { candidate_name, job_title, company_name, ... }
   */
  async renderAgentPrompt(agentId, metadata) {
    const enc = encodeURIComponent(agentId)
    return requestOne('post', `${AI_CALLS}/agents/${enc}/render`, { metadata })
  }

  /**
   * Live ATS has no working PATCH/PUT/GET /api/ai-calls/agents/:id
   * (404 even for AGT_9A85BFD6). Do not probe those routes — Test Call
   * sends resolved prompts + infields on POST /api/ai-calls/initiate instead.
   */
  async updateIndusAgentPrompt() {
    return { status: 'SKIPPED', reason: 'agent_update_by_id_not_deployed' }
  }

  /**
   * Find English Screening/Drive org agents by name. Does not POST create —
   * live ATS 400s ("Failed to create Indus agent"); create is portal or
   * Admin "+ Create agent". Does not PATCH or delete the Hindi agent.
   */
  async ensureEnglishOrgAgents() {
    if (ensureEnglishAgentsInflight) return ensureEnglishAgentsInflight
    ensureEnglishAgentsInflight = this._ensureEnglishOrgAgents().finally(() => {
      ensureEnglishAgentsInflight = null
    })
    return ensureEnglishAgentsInflight
  }

  async _ensureEnglishOrgAgents() {
    const listRes = await this.listIndusAgents()
    const agents = extractLiveIndusAgentsList(listRes)
    const screening = findListedAgentByName(agents, ENGLISH_SCREENING_AGENT_NAME)
    const drive = findListedAgentByName(agents, ENGLISH_DRIVE_AGENT_NAME)
    const screeningId = extractBoundAgentId(screening)
    const driveId = extractBoundAgentId(drive)
    return {
      status: 'OK',
      screeningId,
      driveId,
      created: [],
      errors: [],
      createDisabled: true,
      templatesOnly: !screeningId && !driveId,
    }
  }

  /**
   * Admin-clicked create: POST new English Screening + Drive agents.
   * Always creates new ids (does not reuse AGT_2384D0F2 — that agent was
   * created without language/voice and Indus defaulted to Hi + Indus-hi-maya).
   * On 400, retries once with a short prompt. Does not run on page load.
   */
  async createEnglishOrgAgentsIfMissing({ knownIds = [], forceNew = true } = {}) {
    const specs = [
      {
        callType: AI_CALL_TYPES.FRESH,
        name: ENGLISH_SCREENING_AGENT_NAME,
        firstMessage: DEFAULT_FIRST_MESSAGE,
        systemPrompt: DEFAULT_SYSTEM_PROMPT,
        shortSystemPrompt: SHORT_ENGLISH_SCREENING_SYSTEM,
      },
      {
        callType: AI_CALL_TYPES.DRIVE,
        name: ENGLISH_DRIVE_AGENT_NAME,
        firstMessage: DEFAULT_DRIVE_FIRST_MESSAGE,
        systemPrompt: DEFAULT_DRIVE_SYSTEM_PROMPT,
        shortSystemPrompt: SHORT_ENGLISH_DRIVE_SYSTEM,
      },
    ]
    const listRes = await this.listIndusAgents()
    const agents = extractIndusAgentsList(listRes)
    const beforeIds = new Set(
      (knownIds.length ? knownIds : agents.map(a => extractBoundAgentId(a)))
        .map(id => String(id || '').trim())
        .filter(Boolean),
    )
    const created = []
    const found = {}
    const errors = []
    const skipped = []

    if (!forceNew) {
      specs.forEach((spec) => {
        const match = findListedAgentByName(agents, spec.name)
        if (match) found[spec.callType] = extractBoundAgentId(match)
      })
    }

    for (let i = 0; i < specs.length; i += 1) {
      const spec = specs[i]
      if (!forceNew && found[spec.callType]) {
        skipped.push({ callType: spec.callType, name: spec.name, agent_id: found[spec.callType] })
        continue
      }
      const attempts = [
        { system_prompt: spec.systemPrompt, starting_instructions: spec.firstMessage },
        { system_prompt: spec.shortSystemPrompt, starting_instructions: spec.firstMessage },
      ]
      const createNames = [spec.name, `${spec.name} ${Date.now().toString().slice(-4)}`]
      let id = ''
      let lastRes = null
      let lastPayload = null
      let usedName = spec.name
      for (let n = 0; n < createNames.length && !id; n += 1) {
        usedName = createNames[n]
        for (let a = 0; a < attempts.length; a += 1) {
          lastPayload = buildCreateAgentPayload({
            config: parseAiCallingSchemaConfig(getAiCallingCatalogSnapshot()),
            agent_in: {
              agent_name: usedName,
              agent_type: 'OUTBOUND',
              agent_description: spec.callType === AI_CALL_TYPES.DRIVE
                ? 'English walk-in / hiring drive calls'
                : 'English screening calls for fresh candidates',
            },
            language: 'en',
            voice: DEFAULT_ENGLISH_VOICE_ID,
            realtime: true,
            callType: spec.callType,
            agent_config: {
              ...attempts[a],
              language: 'en',
              voice: DEFAULT_ENGLISH_VOICE_ID,
            },
          })
          lastRes = await this.createIndusAgent(lastPayload)
          id = extractBoundAgentId(lastRes)
          if (isForbiddenEnglishCreateReuseId(id)) {
            id = ''
            continue
          }
          if (id) break
          if (!isAgentCreateHttpError(lastRes)) break
        }
      }
      if (id) {
        found[spec.callType] = id
        created.push({
          callType: spec.callType,
          name: usedName,
          agent_id: id,
          language: 'en',
          voice: DEFAULT_ENGLISH_VOICE_ID,
        })
        if (typeof console !== 'undefined' && console.info) {
          console.info('[AiCallingService] English agent created', {
            name: usedName,
            agent_id: id,
            sent: lastPayload,
          })
        }
        beforeIds.add(id)
        agents.push({ agent_id: id, name: usedName, agent_name: usedName })
      } else {
        errors.push({
          name: spec.name,
          message: getAiCallingErrorMessage(lastRes, `${AI_CALLS}/agents`)
            || (lastRes && lastRes.message)
            || 'Failed to create Indus agent',
          sent: lastPayload,
        })
      }
    }

    if (!found[AI_CALL_TYPES.FRESH] || !found[AI_CALL_TYPES.DRIVE]) {
      const afterRes = await this.listIndusAgents()
      const afterAgents = extractIndusAgentsList(afterRes)
      const newIds = afterAgents
        .map(a => extractBoundAgentId(a))
        .filter(id => id && !beforeIds.has(id) && !Object.values(found).includes(id))
      if (!found[AI_CALL_TYPES.FRESH] && newIds[0]) {
        found[AI_CALL_TYPES.FRESH] = newIds[0]
        created.push({
          callType: AI_CALL_TYPES.FRESH,
          name: ENGLISH_SCREENING_AGENT_NAME,
          agent_id: newIds[0],
          untitled: true,
        })
      }
      if (!found[AI_CALL_TYPES.DRIVE] && newIds[1]) {
        found[AI_CALL_TYPES.DRIVE] = newIds[1]
        created.push({
          callType: AI_CALL_TYPES.DRIVE,
          name: ENGLISH_DRIVE_AGENT_NAME,
          agent_id: newIds[1],
          untitled: true,
        })
      }
    }

    const screeningId = isBrokenHindiConfigAgent(found[AI_CALL_TYPES.FRESH])
      ? ''
      : (found[AI_CALL_TYPES.FRESH] || '')
    const driveId = isBrokenHindiConfigAgent(found[AI_CALL_TYPES.DRIVE])
      ? ''
      : (found[AI_CALL_TYPES.DRIVE] || '')
    return {
      status: errors.length && !created.length && !screeningId ? 'ERROR' : 'OK',
      screeningId,
      driveId,
      created,
      skipped,
      errors,
      templatesOnly: !created.length && !screeningId && !driveId,
    }
  }

  /**
   * Preview TTS audio for a script (uses admin Indus TTS config on server).
   * @param {{ text: string, callType?: string }} payload
   * @returns {Promise<Blob|Object>} audio blob on success, or error envelope
   */
  async previewSpeech(payload) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/preview`,
        payload,
        {
          ...authHeaders(),
          responseType: 'blob',
        },
      )
      if (res.data instanceof Blob) {
        const contentType = res.headers['content-type'] || ''
        if (contentType.includes('application/json')) {
          const text = await res.data.text()
          try {
            return JSON.parse(text)
          } catch (e) {
            return { status: 'ERROR', message: text }
          }
        }
        return res.data
      }
      return res.data
    } catch (err) {
      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          return JSON.parse(text)
        } catch (e) {
          return { status: 'ERROR', message: err.message }
        }
      }
      return normalizeError(err, `${AI_CALLING}/preview`)
    }
  }

  // ── Gemini prompt generation + template library ─────────────────────────

  /**
   * Generate system + first message via Gemini (backend proxy).
   * @param {{ callType: string, brief: string, jobContext?: Object }} payload
   */
  async generatePromptWithGemini(payload) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/prompts/generate`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompts/generate`)
    }
  }

  /**
   * Validate prompt against master template rules.
   * @param {{ callType: string, systemPrompt: string, firstMessage: string }} payload
   */
  async validatePrompt(payload) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/prompts/validate`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompts/validate`)
    }
  }

  /**
   * List prompt templates (optionally filtered by call type).
   * @param {{ call_type?: string }} [params]
   */
  async listPromptTemplates(params = {}) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/prompt-templates`,
        { ...authHeaders(), params },
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompt-templates`)
    }
  }

  /**
   * @param {string|number} templateId
   */
  async getPromptTemplate(templateId) {
    try {
      const res = await axios.get(
        `${API_URL}${AI_CALLING}/prompt-templates/${encodeURIComponent(templateId)}`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompt-templates/:id`)
    }
  }

  /**
   * @param {Object} payload
   */
  async createPromptTemplate(payload) {
    try {
      const res = await axios.post(
        `${API_URL}${AI_CALLING}/prompt-templates`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompt-templates`)
    }
  }

  /**
   * @param {string|number} templateId
   * @param {Object} payload
   */
  async updatePromptTemplate(templateId, payload) {
    try {
      const res = await axios.put(
        `${API_URL}${AI_CALLING}/prompt-templates/${encodeURIComponent(templateId)}`,
        payload,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompt-templates/:id`)
    }
  }

  /**
   * @param {string|number} templateId
   */
  async deletePromptTemplate(templateId) {
    try {
      const res = await axios.delete(
        `${API_URL}${AI_CALLING}/prompt-templates/${encodeURIComponent(templateId)}`,
        authHeaders(),
      )
      return res.data
    } catch (err) {
      return normalizeError(err, `${AI_CALLING}/prompt-templates/:id`)
    }
  }
}

export default new AiCallingService()
